import type { ConferenciaCpmResponse, CustoPessoalResponse, RateioResponse, RateioResumoResponse, SalvarConferenciaCpmDto } from '@triade/contracts';
import { useConsulta, useMutacao } from './base';

/** Rateio de uma competencia (GET /rateios/:competencia): `rateio.ver`; R$ so com `custo.ver`. */
const INVALIDAR = [['rateios'], ['cifs'], ['centros-custo']];

export function useRateio(competencia: string, habilitado = true) {
  return useConsulta<RateioResponse>(['rateios', competencia], `/rateios/${competencia}`, { ativo: habilitado });
}

/** Resumo de todas as competencias da empresa (GET /rateios): `rateio.ver`. Alimenta os pontos do seletor de mes (salvo / XARP). */
export function useRateiosResumo() {
  return useConsulta<RateioResumoResponse[]>(['rateios', 'resumo'], '/rateios');
}

/** Calcula o rateio (POST /rateios/:c/recalcular): `rateio.editar`. */
export function useRecalcularRateio() {
  return useMutacao<RateioResponse, { competencia: string }>({
    metodo: 'POST',
    caminho: (v) => `/rateios/${v.competencia}/recalcular`,
    invalidar: INVALIDAR,
  });
}

/** Salva o rateio calculado (POST /rateios/:c/salvar): fecha e grava o CPM. `rateio.editar`. */
export function useSalvarRateio() {
  return useMutacao<RateioResponse, { competencia: string }>({
    metodo: 'POST',
    caminho: (v) => `/rateios/${v.competencia}/salvar`,
    invalidar: INVALIDAR,
  });
}

/** Reabre o rateio fechado (POST /rateios/:c/reabrir, corpo de confirmacao): FECHADO→ABERTO, destrava as CIFs. `rateio.editar`. */
export function useReabrirRateio() {
  return useMutacao<RateioResponse, { competencia: string }>({
    metodo: 'POST',
    caminho: (v) => `/rateios/${v.competencia}/reabrir`,
    corpo: () => ({ confirmacao: 'REABRIR' }),
    invalidar: INVALIDAR,
  });
}

/** Exclui o rateio da competencia (DELETE /rateios/:c): `rateio.editar`. */
export function useExcluirRateio() {
  return useMutacao<RateioResponse, { competencia: string }>({
    metodo: 'DELETE',
    caminho: (v) => `/rateios/${v.competencia}`,
    invalidar: INVALIDAR,
  });
}

/** Custo de pessoal por centro da competencia, so totais (GET /rateios/:c/custo-pessoal): permissao `pessoal.ver`. */
export function useCustoPessoal(competencia: string) {
  return useConsulta<CustoPessoalResponse>(['custo-pessoal', competencia], `/rateios/${competencia}/custo-pessoal`);
}

/** Conferencia do CPM com a planilha do cliente (GET /rateios/:c/conferencia): `rateio.ver` + `custo.ver`. */
export function useConferenciaCpm(competencia: string, habilitado = true) {
  return useConsulta<ConferenciaCpmResponse>(['rateios', competencia, 'conferencia'], `/rateios/${competencia}/conferencia`, { ativo: habilitado });
}

/** Grava a referencia de CPM e a explicacao por centro e a tolerancia (PUT /rateios/:c/conferencia): `rateio.editar` + `custo.ver`. */
export function useSalvarConferenciaCpm() {
  return useMutacao<ConferenciaCpmResponse, { competencia: string; dto: SalvarConferenciaCpmDto }>({
    metodo: 'PUT',
    caminho: (v) => `/rateios/${v.competencia}/conferencia`,
    corpo: (v) => v.dto,
    invalidar: [['rateios']],
  });
}
